import { useCallback, useEffect, useState, memo } from 'react';
import styled from 'styled-components';
import { Send } from 'lucide-react';

import { useModal } from '@hooks/useModal';
import { useCurrentUser } from '@redux/selectors/users';
import { useAppDispatch } from '@redux/store';
import { useSendChatMessageMutation } from '@redux/queries/chat';
import { useUpdateUserSettingsMutation } from '@redux/queries/user';
import { chatSlice } from '@redux/state/chatSlice';
import theme from 'theme';
import { ChatMessageType, ChatMessage } from '@typings/show';
import TextInput from '@components/fields/input';
import TouchableOpacity from '@components/buttons/TouchableOpacity';
import { PremierText } from '@components/text';

import ColorPickerModal from './ColorPickerModal';
import GifsModal from './GifsModal';

export const DEFAULT_USERNAME_COLOR = theme.colors.white;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: center;
  padding: 0 8px 12px;
`;

const ColorPickerButton = styled.button<{
  $backgroundColor: string;
}>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.white};
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const InputBlock = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
`;

const GifButton = styled(TouchableOpacity)`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const GifText = styled(PremierText)`
  font-size: ${({ theme }) => theme.fonts.fontSize.lg};
`;

const SendButton = styled(TouchableOpacity)`
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

type Props = {
  showId: string;
};

const ChatForm = ({ showId }: Props) => {
  const [content, setContent] = useState<string>('');
  const [optimisticMessageContent, setOptimisticMessageContent] =
    useState<string>('');
  const currentUser = useCurrentUser();
  const {
    isModalShown: isColorPickerModalShown,
    showModal: showColorPickerModal,
    hideModal: hideColorPickerModal,
  } = useModal();
  const {
    isModalShown: isGifModalShown,
    showModal: showGifModal,
    hideModal: hideGifModal,
  } = useModal();
  const [sendChatMessage, { isLoading, isSuccess, isError }] =
    useSendChatMessageMutation();
  const [updateUsernameColor] = useUpdateUserSettingsMutation();
  const dispatch = useAppDispatch();

  const onGifPress = useCallback(() => {
    showGifModal();
  }, [showGifModal]);

  const onColorPickerPress = useCallback(() => {
    showColorPickerModal();
  }, [showColorPickerModal]);

  const onSelectGif = useCallback(
    (url: string) => {
      if (!url && !showId) {
        return;
      }
      sendChatMessage({ showId, content: url, type: ChatMessageType.Gif });
    },
    [sendChatMessage, showId],
  );

  const onSelectColor = useCallback(
    (hex: string) => {
      if (!hex || hex === currentUser?.usernameColor) {
        return;
      }
      updateUsernameColor({ usernameColor: hex });
    },
    [updateUsernameColor, currentUser?.usernameColor],
  );

  const onSubmit = useCallback(() => {
    const trimmedContent = content?.trim();
    if (!trimmedContent || !showId || isLoading || !currentUser?.id) {
      return;
    }
    const optimisticMessage: ChatMessage = {
      id: String(new Date().getTime()),
      type: ChatMessageType.Text,
      content: trimmedContent,
      showId,
      userId: currentUser?.id,
      createdAt: new Date().toISOString() as unknown as Date,
    };
    setOptimisticMessageContent(trimmedContent);
    setContent('');
    dispatch(chatSlice.actions.setChatMessageById(optimisticMessage));
    dispatch(
      chatSlice.actions.addMessageToShowIds({
        showId,
        id: optimisticMessage.id,
      }),
    );

    sendChatMessage({
      showId,
      content: trimmedContent,
      type: ChatMessageType.Text,
      optimisticMessageId: optimisticMessage.id,
    });
  }, [showId, content, isLoading, currentUser?.id, dispatch, sendChatMessage]);

  useEffect(() => {
    if (isSuccess) {
      setOptimisticMessageContent('');
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError && optimisticMessageContent) {
      setContent(optimisticMessageContent);
    }
  }, [isError, optimisticMessageContent]);

  return (
    <>
      <Container>
        <ColorPickerButton
          onClick={onColorPickerPress}
          $backgroundColor={
            currentUser?.usernameColor || DEFAULT_USERNAME_COLOR
          }
          disabled={isLoading}
        />

        <InputBlock>
          <TextInput
            placeholder='Start typing'
            onChangeText={setContent}
            value={content}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onSubmit();
              }
            }}
          />

          <GifButton onClick={onGifPress} disabled={isLoading}>
            <GifText>GIF</GifText>
          </GifButton>
        </InputBlock>

        <SendButton onClick={onSubmit} disabled={isLoading}>
          <Send size={24} color={theme.colors.white} />
        </SendButton>
      </Container>

      <ColorPickerModal
        currentColor={currentUser?.usernameColor || DEFAULT_USERNAME_COLOR}
        isColorPickerModalShown={isColorPickerModalShown}
        hideColorPickerModal={hideColorPickerModal}
        onSelectColor={onSelectColor}
      />

      <GifsModal
        isGifModalShown={isGifModalShown}
        hideGifModal={hideGifModal}
        onSelectGif={onSelectGif}
      />
    </>
  );
};

export default memo(ChatForm);
